// Iter 146 — Data Sync: preview → production database mirror.
//
// Export a gzipped archive of all business data from this environment,
// carry the file to the other environment (after Deploy), upload it and
// press Mirror. Overwrites the target's data; environment-specific
// settings (SSO issuer URLs) are restored automatically and all sessions
// are wiped. Super-admin only.

import { useEffect, useRef, useState } from "react";
import api, { API } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import {
  DatabaseZap, Download, Upload, Loader2, AlertTriangle, CheckCircle2,
  ChevronRight, ChevronDown,
} from "lucide-react";

const CHUNK = 4 * 1024 * 1024;
const fmt = (n) => (n ?? 0).toLocaleString();

export default function DataSync() {
  const fileRef = useRef(null);
  const [catalog, setCatalog] = useState(null);
  const [sel, setSel] = useState([]);           // selected collection names
  const [open, setOpen] = useState([]);         // expanded group ids
  const [exporting, setExporting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [preview, setPreview] = useState(null);
  const [applying, setApplying] = useState(false);
  const [report, setReport] = useState(null);
  const [access, setAccess] = useState(null);   // null = checking

  useEffect(() => {
    api.get("/data-sync/operators")
      .then((r) => setAccess(!!r.data.is_operator))
      .catch(() => setAccess(false));
    api.get("/data-sync/collections")
      .then((r) => setCatalog(r.data))
      .catch(() => setCatalog(null));
  }, []);

  const docsOf = (name) =>
    catalog?.collections?.find((c) => c.collection === name)?.docs ?? 0;
  const selDocs = sel.reduce((n, c) => n + docsOf(c), 0);
  const allCols = catalog?.collections?.map((c) => c.collection) || [];

  const toggleCols = (names, on) =>
    setSel((prev) => (on ? [...new Set([...prev, ...names])]
      : prev.filter((c) => !names.includes(c))));

  const doExport = async () => {
    if (!sel.length) { toast.error("Pick at least one bundle or collection to export"); return; }
    setExporting(true);
    try {
      const partial = sel.length !== allCols.length;
      const qs = partial ? `?collections=${encodeURIComponent(sel.join(","))}` : "";
      const res = await fetch(`${API}/data-sync/export${qs}`, { credentials: "include" });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const cd = res.headers.get("content-disposition") || "";
      const name = /filename="([^"]+)"/.exec(cd)?.[1] || "conduit-data-sync.jsonl.gz";
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = name;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(`Exported ${(blob.size / 1024 / 1024).toFixed(1)} MB`);
    } catch (e) {
      toast.error(`Export failed: ${e.message}`);
    } finally {
      setExporting(false);
    }
  };

  const doUpload = async (file) => {
    setUploading(true);
    setPreview(null);
    setReport(null);
    setProgress(0);
    try {
      const { data: begin } = await api.post("/data-sync/upload/begin");
      const uid = begin.upload_id;
      for (let off = 0; off < file.size; off += CHUNK) {
        const slice = file.slice(off, off + CHUNK);
        await api.post(`/data-sync/upload/chunk?upload_id=${uid}`, slice, {
          headers: { "Content-Type": "application/octet-stream" },
        });
        setProgress(Math.min(100, Math.round(((off + CHUNK) / file.size) * 100)));
      }
      const { data } = await api.post("/data-sync/upload/finish", { upload_id: uid });
      setPreview(data);
      if (!data.integrity_ok) toast.warning("Archive integrity mismatch — see details");
    } catch (e) {
      toast.error(e.response?.data?.detail || `Upload failed: ${e.message}`);
    } finally {
      setUploading(false);
    }
  };

  const doApply = async () => {
    const total = preview?.total_docs ?? 0;
    const n = preview?.comparison?.length ?? 0;
    const body = preview?.partial
      ? `MIRROR ${n} COLLECTION${n === 1 ? "" : "S"}?\n\nThis is a PARTIAL archive. Only these ${n} collections will be replaced in database "${preview?.target_db}" with the ${total} documents from "${preview?.source_db}".\nEvery other collection in this database is left untouched.\nAll sessions will be wiped (everyone re-logs in). This cannot be undone.`
      : `MIRROR NOW?\n\nThis REPLACES the data in database "${preview?.target_db}" with the ${total} documents from "${preview?.source_db}".\nAll sessions will be wiped (everyone re-logs in). This cannot be undone.`;
    // eslint-disable-next-line no-alert
    if (!window.confirm(body)) return;
    setApplying(true);
    try {
      const { data } = await api.post("/data-sync/apply", { upload_id: preview.upload_id });
      setReport(data);
      toast.success("Mirror applied — all sessions wiped, you will be signed out.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Apply failed");
    } finally {
      setApplying(false);
    }
  };

  return (
    <div className="space-y-4" data-testid="data-sync-page">
      <PageHeader
        icon={DatabaseZap}
        title="Data Sync"
        subtitle="Mirror this environment's data into the other one (preview ⇄ production) via an archive file. One-way, overwrites the target."
      />

      {access === false && (
        <Card className="border-rose-200 bg-rose-50 p-6 text-sm text-rose-800" data-testid="data-sync-not-operator">
          <div className="font-semibold">You are not a Data Sync operator</div>
          <p className="mt-1 text-xs">
            Database mirroring is restricted to named operators. A super admin can grant access in
            Settings → Security → Data Sync operators.
          </p>
        </Card>
      )}

      {access !== false && (
      <>
      <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800" data-testid="data-sync-warning">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
        <span>
          Applying an archive <b>replaces</b> every included collection in the target database. Logs, sessions, tokens
          and signing keys are never transferred; environment-specific SSO URLs are restored automatically after the mirror.
        </span>
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Card className="p-4">
          <div className="mb-1 text-sm font-semibold text-slate-800">1 · Export from this environment</div>
          <p className="mb-3 text-xs text-slate-500">
            Choose exactly what to move. Nothing is selected by default — tick a bundle, or expand it
            to pick individual collections.
          </p>

          {!catalog && (
            <div className="flex items-center gap-2 text-xs text-slate-500" data-testid="data-sync-catalog-loading">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading collections…
            </div>
          )}

          {catalog && (
            <>
              <div className="mb-2 flex items-center gap-2 text-xs">
                <button
                  type="button" className="text-blue-600 hover:underline"
                  onClick={() => setSel(allCols)} data-testid="data-sync-select-all"
                >Select everything</button>
                <span className="text-slate-300">·</span>
                <button
                  type="button" className="text-blue-600 hover:underline"
                  onClick={() => setSel([])} data-testid="data-sync-clear-selection"
                >Clear</button>
              </div>

              <div className="mb-3 divide-y divide-slate-100 rounded border border-slate-200" data-testid="data-sync-bundles">
                {catalog.groups.filter((g) => g.collections.length).map((g) => {
                  const chosen = g.collections.filter((c) => sel.includes(c));
                  const all = chosen.length === g.collections.length;
                  const isOpen = open.includes(g.id);
                  return (
                    <div key={g.id} className="px-2 py-1.5">
                      <div className="flex items-start gap-2">
                        <input
                          type="checkbox" className="mt-1 h-3.5 w-3.5 cursor-pointer accent-blue-600"
                          checked={all}
                          ref={(el) => { if (el) el.indeterminate = !all && chosen.length > 0; }}
                          onChange={(e) => toggleCols(g.collections, e.target.checked)}
                          data-testid={`data-sync-group-${g.id}`}
                        />
                        <div className="min-w-0 flex-1">
                          <button
                            type="button"
                            className="flex w-full items-center gap-1 text-left"
                            onClick={() => setOpen((p) => (isOpen ? p.filter((x) => x !== g.id) : [...p, g.id]))}
                            data-testid={`data-sync-group-toggle-${g.id}`}
                          >
                            {isOpen ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />}
                            <span className="text-xs font-semibold text-slate-800">{g.label}</span>
                            <span className="text-[10px] text-slate-500">
                              {g.collections.length} collections · {fmt(g.docs)} docs
                              {chosen.length && !all ? ` · ${chosen.length} picked` : ""}
                            </span>
                          </button>
                          <div className="ml-4 text-[10px] text-slate-400">{g.note}</div>
                          {isOpen && (
                            <div className="ml-4 mt-1 grid gap-0.5 sm:grid-cols-2">
                              {g.collections.map((c) => (
                                <label key={c} className="flex items-center gap-1.5 text-[11px] text-slate-600">
                                  <input
                                    type="checkbox" className="h-3 w-3 cursor-pointer accent-blue-600"
                                    checked={sel.includes(c)}
                                    onChange={(e) => toggleCols([c], e.target.checked)}
                                    data-testid={`data-sync-col-${c}`}
                                  />
                                  <span className="truncate font-mono">{c}</span>
                                  <span className="text-slate-400">{fmt(docsOf(c))}</span>
                                </label>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </>
          )}

          <Button onClick={doExport} disabled={exporting || !sel.length} data-testid="data-sync-export-btn">
            {exporting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}
            {sel.length
              ? `Export ${sel.length} collection${sel.length === 1 ? "" : "s"} (${fmt(selDocs)} docs)`
              : "Export archive"}
          </Button>
          {catalog && !sel.length && (
            <div className="mt-2 text-[11px] text-slate-500" data-testid="data-sync-export-hint">
              Select at least one bundle or collection to enable the export.
            </div>
          )}
        </Card>

        <Card className="p-4">
          <div className="mb-1 text-sm font-semibold text-slate-800">2 · Import into this environment</div>
          <p className="mb-3 text-xs text-slate-500">
            Upload an archive exported from the other environment, review the comparison, then mirror.
          </p>
          <input
            ref={fileRef} type="file" accept=".gz,.jsonl.gz,application/gzip" className="hidden"
            onChange={(e) => e.target.files?.[0] && doUpload(e.target.files[0])}
            data-testid="data-sync-file-input"
          />
          <Button variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="data-sync-upload-btn">
            {uploading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Upload className="mr-1 h-4 w-4" />}
            {uploading ? `Uploading… ${progress}%` : "Choose archive file"}
          </Button>
        </Card>
      </div>

      {preview && !report && (
        <Card className="p-4" data-testid="data-sync-preview">
          <div className="mb-2 flex flex-wrap items-center gap-2 text-sm">
            <span className="font-semibold text-slate-800">Archive review</span>
            <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">source DB: <b>{preview.source_db}</b></span>
            <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">target DB: <b>{preview.target_db}</b></span>
            <span className="rounded bg-slate-100 px-2 py-0.5 text-xs">exported {preview.exported_at?.slice(0, 19)}</span>
            {preview.partial && (
              <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs font-semibold text-indigo-800" data-testid="data-sync-partial-badge">
                PARTIAL archive
              </span>
            )}
            {preview.integrity_ok
              ? <span className="inline-flex items-center gap-1 rounded bg-emerald-100 px-2 py-0.5 text-xs text-emerald-800"><CheckCircle2 className="h-3 w-3" /> integrity OK</span>
              : <span className="rounded bg-rose-100 px-2 py-0.5 text-xs text-rose-800">integrity MISMATCH</span>}
            <span className="text-xs text-slate-500">{preview.total_docs} documents in {preview.comparison.length} collections</span>
          </div>
          {preview.partial && (
            <div className="mb-2 flex items-start gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900" data-testid="data-sync-partial-note">
              <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
              <span>
                This is a <b>partial</b> archive — only the {preview.comparison.length} collection
                {preview.comparison.length === 1 ? "" : "s"} listed below will be replaced.
                Every other collection in <b>{preview.target_db}</b> is left exactly as it is.
              </span>
            </div>
          )}
          <div className="max-h-80 overflow-auto rounded border border-slate-200">
            <table className="w-full text-left text-xs">
              <thead className="sticky top-0 bg-slate-50 text-[10px] uppercase tracking-wide text-slate-500">
                <tr><th className="px-3 py-1.5">Collection</th><th className="px-3 py-1.5 text-right">In archive</th><th className="px-3 py-1.5 text-right">Currently here</th></tr>
              </thead>
              <tbody>
                {preview.comparison.map((r) => (
                  <tr key={r.collection} className={`border-t border-slate-100 ${r.archive_docs !== r.current_docs ? "bg-amber-50/60" : ""}`}>
                    <td className="px-3 py-1 font-mono">{r.collection}</td>
                    <td className="px-3 py-1 text-right">{r.archive_docs}</td>
                    <td className="px-3 py-1 text-right">{r.current_docs}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Button className="mt-3 bg-rose-600 hover:bg-rose-700" onClick={doApply} disabled={applying} data-testid="data-sync-apply-btn">
            {applying ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <DatabaseZap className="mr-1 h-4 w-4" />}
            {preview.partial ? "Mirror these collections only" : "Mirror now (overwrite this database)"}
          </Button>
        </Card>
      )}

      {report && (
        <Card className="p-4" data-testid="data-sync-report">
          <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-emerald-700">
            <CheckCircle2 className="h-4 w-4" /> Mirror applied
          </div>
          <div className="text-xs text-slate-600">
            {Object.keys(report.collections).length} collections replaced ·
            sessions/tokens wiped ({Object.entries(report.wiped || {}).map(([k, v]) => `${k}:${v}`).join(", ")}) ·
            settings keys restored: {report.settings_keys_restored?.join(", ") || "none"}
          </div>
          <div className="mt-2 text-xs font-medium text-amber-700">Everyone (including you) must sign in again.</div>
        </Card>
      )}
      </>
      )}
    </div>
  );
}
